import { configureStore, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { TypedUseSelectorHook, useDispatch, useSelector } from "react-redux";
import {
  applyThemeToDocument,
  DEFAULT_THEME,
  persistTheme,
  ThemeDirection,
  ThemeMode,
  ThemePreferences,
} from "lib/theme";
import { resolvePageFromLocation } from "lib/routes";

export const MANAGE_TOOLS_SESSION_KEY = "manage_tools_active_tool";

interface SidebarState {
  sidebar: boolean;
  isMobile: boolean;
  page: string;
  popupOpen: string | boolean;
  manageToolsActiveTool: string | null;
  frontTab: string | null;
  themeMode: ThemeMode;
  themeDirection: ThemeDirection;
  themeColor: string;
  settingsOpen: boolean;
}

const initialState: SidebarState = {
  sidebar: false,
  isMobile: false,
  page: resolvePageFromLocation(),
  popupOpen: false,
  manageToolsActiveTool: null,
  frontTab: null,
  themeMode: DEFAULT_THEME.mode,
  themeDirection: DEFAULT_THEME.direction,
  themeColor: DEFAULT_THEME.color,
  settingsOpen: false,
};

function themePrefsFromState(state: SidebarState): ThemePreferences {
  return {
    mode: state.themeMode,
    direction: state.themeDirection,
    color: state.themeColor,
  };
}

function syncTheme(state: SidebarState) {
  const prefs = themePrefsFromState(state);
  persistTheme(prefs);
  applyThemeToDocument(prefs);
}

const storeSlice = createSlice({
  name: "store",
  initialState,
  reducers: {
    toggleSidebar: (state) => {
      state.sidebar = !state.sidebar;
    },
    setIsMobile: (state, action: PayloadAction<boolean>) => {
      state.isMobile = action.payload;
    },
    setPage: (state, action: PayloadAction<string>) => {
      state.page = action.payload;
    },
    setPopupOpen: (state, action: PayloadAction<string | boolean>) => {
      state.popupOpen = action.payload;
    },
    setFrontTab: (state, action: PayloadAction<string>) => {
      state.frontTab = action.payload;
    },
    setManageToolsSession: (
      state,
      action: PayloadAction<{ tool: string | null; writeSession?: boolean }>,
    ) => {
      state.manageToolsActiveTool = action.payload.tool;
      if (typeof window === "undefined" || action.payload.writeSession === false)
        return;
      if (action.payload.tool) {
        sessionStorage.setItem(MANAGE_TOOLS_SESSION_KEY, action.payload.tool);
      } else {
        sessionStorage.removeItem(MANAGE_TOOLS_SESSION_KEY);
      }
    },
    hydrateTheme: (state, action: PayloadAction<ThemePreferences>) => {
      state.themeMode = action.payload.mode;
      state.themeDirection = action.payload.direction;
      state.themeColor = action.payload.color;
    },
    setThemeMode: (state, action: PayloadAction<ThemeMode>) => {
      state.themeMode = action.payload;
      syncTheme(state);
    },
    setThemeDirection: (state, action: PayloadAction<ThemeDirection>) => {
      state.themeDirection = action.payload;
      syncTheme(state);
    },
    setThemeColor: (state, action: PayloadAction<string>) => {
      state.themeColor = action.payload;
      syncTheme(state);
    },
    resetTheme: (state) => {
      state.themeMode = DEFAULT_THEME.mode;
      state.themeDirection = DEFAULT_THEME.direction;
      state.themeColor = DEFAULT_THEME.color;
      syncTheme(state);
    },
    setSettingsOpen: (state, action: PayloadAction<boolean>) => {
      state.settingsOpen = action.payload;
    },
    toggleSettings: (state) => {
      state.settingsOpen = !state.settingsOpen;
    },
  },
});

export const {
  toggleSidebar,
  setIsMobile,
  setPage,
  setPopupOpen,
  setFrontTab,
  setManageToolsSession,
  hydrateTheme,
  setThemeMode,
  setThemeDirection,
  setThemeColor,
  resetTheme,
  setSettingsOpen,
  toggleSettings,
} = storeSlice.actions;

export const store = configureStore({
  reducer: {
    store: storeSlice.reducer,
  },
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

export const useStore = () => {
  const sidebar = useAppSelector((state) => state.store.sidebar);
  const isMobile = useAppSelector((state) => state.store.isMobile);
  const page = useAppSelector((state) => state.store.page);
  const popupOpen = useAppSelector((state) => state.store.popupOpen);
  const manageToolsActiveTool = useAppSelector(
    (state) => state.store.manageToolsActiveTool,
  );
  const frontTab = useAppSelector((state) => state.store.frontTab);
  const themeMode = useAppSelector((state) => state.store.themeMode);
  const themeDirection = useAppSelector((state) => state.store.themeDirection);
  const themeColor = useAppSelector((state) => state.store.themeColor);
  const settingsOpen = useAppSelector((state) => state.store.settingsOpen);
  const dispatch = useAppDispatch();

  return {
    sidebar,
    isMobile,
    page,
    popupOpen,
    manageToolsActiveTool,
    frontTab,
    themeMode,
    themeDirection,
    themeColor,
    settingsOpen,
    toggleSidebar: () => dispatch(toggleSidebar()),
    setIsMobile: (value: boolean) => dispatch(setIsMobile(value)),
    setPage: (value: string) => dispatch(setPage(value)),
    setPopupOpen: (value: string | boolean) => dispatch(setPopupOpen(value)),
    setFrontTab: (value: string) => dispatch(setFrontTab(value)),
    setManageToolsActiveTool: (tool: string | null, writeSession = true) => {
      dispatch(setManageToolsSession({ tool, writeSession }));
    },
    hydrateTheme: (prefs: ThemePreferences) => dispatch(hydrateTheme(prefs)),
    setThemeMode: (mode: ThemeMode) => dispatch(setThemeMode(mode)),
    setThemeDirection: (direction: ThemeDirection) =>
      dispatch(setThemeDirection(direction)),
    setThemeColor: (color: string) => dispatch(setThemeColor(color)),
    resetTheme: () => dispatch(resetTheme()),
    setSettingsOpen: (open: boolean) => dispatch(setSettingsOpen(open)),
    toggleSettings: () => dispatch(toggleSettings()),
  };
};
